<template>
    <div class="gap-6 overflow-hidden lg:grid lg:grid-cols-2 xl:gap-16">
            <div class="space-y-4 lg:space-y-8">
                <button
                    :class="['flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!', {'text-primary': mode === 'request'}]"
                    @click="mode='request'"
                >
                    <NuxtPicture
                        src="/images/icons/icon-envelope.svg"
                        alt="Anfrageformular"
                    />
                    <span>Anfrage</span>
                </button>
                <button
                    class="flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!"
                    @click="placeCall()"
                >
                    <NuxtPicture
                        src="/images/icons/icon-phone.svg"
                        alt="Direktanruf"
                    />
                    <span>Direktanruf</span>
                </button>
                <button
                    :class="['flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!', {'text-primary': mode === 'callback'}]"
                    @click="mode='callback'"
                >
                    <NuxtPicture
                        src="/images/icons/icon-recall-service.svg"
                        alt="Rückrufservice"
                    />
                    <span>Rückrufservice</span>
                </button>
                <IconStickFigurePhone class="hidden lg:block"/>
            </div>

            <!-- Vee-Validate v4 -->
            <form
                class="contact-form space-y-4 py-4 text-xs lg:space-y-8 lg:py-0 lg:text-base"
                @submit.prevent="sendMessage"
            >
                <div class="space-y-4">
                    <UFormField
                        label="Ihre E-Mail-Adresse"
                        :required="mode === 'request'"
                    >
                        <UInput
                            v-model="email"
                            placeholder="E-Mail-Adresse"
                            type="email"
                            class="w-full"
                        />
                    </UFormField>
                    <UFormField
                        label="Ihre Telefonnummer"
                        :required="mode === 'callback'"
                    >
                        <UInput
                            v-model="phone"
                            placeholder="Telefonnummer"
                            type="tel"
                            class="w-full"
                        />
                    </UFormField>
                    <UFormField
                        label="Ihre Postleitzahl"
                    >
                        <UInput
                            v-model="zip"
                            placeholder="Postleitzahl"
                            type="number"
                            class="w-full"
                        />
                    </UFormField>
                    <UFormField
                        label="Ihre Nachricht an uns"
                        required
                    >
                        <UTextarea
                            v-model="message"
                            :rows="6"
                            placeholder="Ihre Nachricht"
                            class="w-full"
                        />
                    </UFormField>
                    <UCheckbox
                        v-model="privacy"
                        size="xl"
                        variant="list"
                        required
                        label="Ich habe die Datenschutzhinweise zur Kenntnis genommen!"
                    />
                    <span
                        v-if="errors.privacy"
                        class="block text-sm text-error mb-2"
                    >{{ errors.privacy }}</span>
                </div>

                <div class="block text-xs">
                    Bei den mit einem Sternchen (<span class="text-secondary">*</span>) markierten Feldern handelt es sich um Pflichtfelder!
                </div>

                <button
                    class="w-full bg-primary text-white flex! gap-2 justify-center py-2"
                    type="submit"
                >
                    Nachricht absenden
                    <IconPaperPlane class="w-6 h-6 fill-white"/>
                </button>
            </form>
        </div>
</template>

<script setup lang="ts">
import {computed, ref, watchEffect} from 'vue';
import {z} from 'zod';
import {useForm, useField} from 'vee-validate';
import {toTypedSchema} from '@vee-validate/zod';
import IconStickFigurePhone from '~/components/Icons/IconStickFigurePhone.vue';
import IconPaperPlane from '~/components/Icons/IconPaperPlane.vue';

const {public: pub} = useRuntimeConfig();
const props = defineProps<{ article: any }>();
const emit = defineEmits<{(e: 'close'): void
    (e: 'sent', payload: { success: boolean }): void
}>();

const mode = ref<'request' | 'callback'>('request');
const zip = ref('');

function placeCall() {
    document.location.assign('tel:+49295576061830');
}

/**
 * Validation: dynamisch abhängig von mode
 */
const schema = computed(() => toTypedSchema(
    z.object({
        email: mode.value === 'request'
            ? z.string()
                .email('Bitte gültige E-Mail-Adresse eingeben.')
            : z.string()
                .optional()
                .or(z.literal('')),
        phone: mode.value === 'callback'
            ? z.string()
                .min(3, 'Bitte Telefonnummer eingeben.')
            : z.string()
                .optional()
                .or(z.literal('')),
        message: z.string()
            .min(1, 'Nachricht ist erforderlich.'),
        privacy: z.literal(true, {errorMap: () => ({message: 'Bitte Datenschutzhinweise bestätigen.'})}),
    }),
));

const {handleSubmit, errors} = useForm({
    validationSchema: schema,
});

// Fields = Single Source of Truth
const {value: email} = useField<string>('email');
const {value: phone} = useField<string>('phone');
const {value: message} = useField<string>('message');
const {value: privacy} = useField<boolean>('privacy');

// Default Message
watchEffect(() => {
    if (!props.article?.articleNumber) return;
    if (!message.value) {
        message.value = `Ich erbitte um Zusendung von tagesaktuellen DWG- oder DXF-Dateien für den Artikel Nr. ${props.article.articleNumber}. Vielen Dank im Voraus!`;
    }
});

const sendMessage = handleSubmit(async (values) => {
    const response = await $fetch<{ sent: boolean }>(`${pub.apiUrl}/api/contact`, {
        method: 'POST',
        body: {
            mode: mode.value,
            zip: zip.value,
            ...values, // email, phone, message, privacy
            article: `${props.article.articleNumber} - ${props.article.name} ${props.article.name2}`,
        },
    });

    emit('sent', {success: !!response?.sent});
});
</script>
